import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Clock, Briefcase, Heart, Mountain, Sun,
  Compass, Users, Sparkles, GraduationCap, Home, Coffee, Globe2,
  Search, X, Mail,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HERO = "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/4p3qhcbe_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-3.jpg";

const VALUES = [
  {
    icon: Heart,
    title: { en: "Hospitality first", fr: "L'hospitalité d'abord", es: "La hospitalidad primero" },
    text: {
      en: "We are nothing without the gesture of welcome. From the first mint tea to the last goodbye, every encounter carries the weight of our family name.",
      fr: "Nous ne sommes rien sans le geste de l'accueil. Du premier thé à la menthe au dernier adieu, chaque rencontre porte le poids de notre nom de famille.",
      es: "No somos nada sin el gesto de la bienvenida. Del primer té a la menta al último adiós, cada encuentro lleva el peso de nuestro apellido.",
    },
  },
  {
    icon: Users,
    title: { en: "Family before company", fr: "Famille avant entreprise", es: "Familia antes que empresa" },
    text: {
      en: "Three hundred families work with Grup Xaluca. Every team member is part of one of them. We protect each other before we protect a target.",
      fr: "Trois cents familles travaillent avec le Grup Xaluca. Chaque collaborateur en fait partie. Nous nous protégeons les uns les autres avant de protéger un objectif.",
      es: "Trescientas familias trabajan con Grup Xaluca. Cada miembro del equipo forma parte de una de ellas. Nos protegemos antes de proteger una cifra.",
    },
  },
  {
    icon: Mountain,
    title: { en: "Of the place, for the place", fr: "Du lieu, pour le lieu", es: "Del lugar, para el lugar" },
    text: {
      en: "We hire local first, train inside, and buy from the valley next door. Hospitality born in the Tafilalet stays in the Tafilalet.",
      fr: "Nous recrutons d'abord localement, formons en interne et achetons dans la vallée d'à côté. L'hospitalité née au Tafilalet reste au Tafilalet.",
      es: "Contratamos primero localmente, formamos internamente y compramos en el valle vecino. La hospitalidad nacida en el Tafilalet se queda en el Tafilalet.",
    },
  },
  {
    icon: Sparkles,
    title: { en: "Craft, not shortcuts", fr: "L'artisanat, pas les raccourcis", es: "Oficio, no atajos" },
    text: {
      en: "Adobe walls take longer than concrete. A hand-rolled couscous beats a machine-made one. We choose the slower way every time it produces a better moment.",
      fr: "Les murs en adobe prennent plus de temps que le béton. Un couscous roulé main vaut mieux qu'un couscous industriel. Nous choisissons la voie lente chaque fois qu'elle produit un meilleur moment.",
      es: "Los muros de adobe llevan más tiempo que el hormigón. Un cuscús enrollado a mano supera al industrial. Elegimos la vía lenta siempre que produzca un mejor momento.",
    },
  },
  {
    icon: GraduationCap,
    title: { en: "Learn forever", fr: "Apprendre toujours", es: "Aprender siempre" },
    text: {
      en: "From kitchen apprentices to head concierges — every role has a path. Languages, sommelier school, spa-therapist diplomas, leadership programmes. We pay for them.",
      fr: "De l'apprenti cuisine au chef de conciergerie — chaque poste a son chemin. Langues, école de sommelier, diplômes spa, programmes de leadership. Nous les finançons.",
      es: "Del aprendiz de cocina al jefe de conserjería — cada puesto tiene su camino. Idiomas, sumiller, diplomas de spa, programas de liderazgo. Los pagamos nosotros.",
    },
  },
  {
    icon: Compass,
    title: { en: "Quietly excellent", fr: "Discrètement excellent", es: "Discretamente excelente" },
    text: {
      en: "We do not chase awards. We chase the moment a guest stops talking and just looks at the dunes. Excellence here is whispered, not shouted.",
      fr: "Nous ne courons pas après les prix. Nous courons après l'instant où un hôte se tait et regarde les dunes. L'excellence ici se chuchote, ne s'annonce pas.",
      es: "No perseguimos premios. Perseguimos el momento en que un huésped se calla y simplemente mira las dunas. La excelencia aquí se susurra, no se grita.",
    },
  },
];

const BENEFITS = [
  {
    icon: Home,
    title: { en: "Housing & meals on-site", fr: "Logement & repas sur place", es: "Alojamiento y comidas en el lugar" },
    text: { en: "Staff quarters at every property — three meals a day from the same kitchens our guests eat from.", fr: "Logements à chaque adresse — trois repas par jour des mêmes cuisines que nos hôtes.", es: "Alojamientos en cada dirección — tres comidas al día de las mismas cocinas que nuestros huéspedes." },
  },
  {
    icon: GraduationCap,
    title: { en: "Funded training & languages", fr: "Formation & langues financées", es: "Formación e idiomas financiados" },
    text: { en: "From hospitality school scholarships to FR / EN / ES / DE / Berber language classes, we pay your tuition.", fr: "Des bourses pour l'école hôtelière aux cours de FR / EN / ES / DE / berbère, nous prenons en charge.", es: "De becas para escuela hotelera a clases de FR / EN / ES / DE / bereber, nosotros pagamos." },
  },
  {
    icon: Coffee,
    title: { en: "Rotation across properties", fr: "Rotation entre les adresses", es: "Rotación entre direcciones" },
    text: { en: "Spend a season in the dunes, the next in the cedar woods, the next in a kasbah — if you want to grow, the doors are open.", fr: "Une saison dans les dunes, la suivante dans les cèdres, la suivante en kasbah — si vous voulez évoluer, les portes sont ouvertes.", es: "Una temporada en las dunas, otra en los cedros, otra en una kasbah — si quiere crecer, las puertas están abiertas." },
  },
  {
    icon: Globe2,
    title: { en: "International exposure", fr: "Ouverture internationale", es: "Apertura internacional" },
    text: { en: "Welcome 100 000 guests a year from 40 countries — your language skills, your worldview, your network grow with every season.", fr: "Accueillez 100 000 hôtes par an venus de 40 pays — vos langues, votre regard, votre réseau grandissent à chaque saison.", es: "Reciba a 100 000 huéspedes al año de 40 países — sus idiomas, su mirada, su red crecen cada temporada." },
  },
  {
    icon: Heart,
    title: { en: "Healthcare & family support", fr: "Santé & soutien aux familles", es: "Salud y apoyo familiar" },
    text: { en: "Group health insurance, parental leave for both parents, schooling support for staff children.", fr: "Assurance santé du groupe, congé parental pour les deux parents, soutien scolaire pour les enfants des équipes.", es: "Seguro de salud del grupo, baja parental para ambos padres, apoyo escolar para los hijos del equipo." },
  },
  {
    icon: Sun,
    title: { en: "A life in the south", fr: "Une vie au sud", es: "Una vida en el sur" },
    text: { en: "Dunes at sunrise, cedar forests on your day off, palm groves at sundown. The south of Morocco is the perk no salary can buy.", fr: "Dunes au lever du soleil, cédraies en jour de repos, palmeraies au crépuscule. Le sud du Maroc est l'avantage qu'aucun salaire ne peut acheter.", es: "Dunas al amanecer, cedrales en su día libre, palmerales al atardecer. El sur de Marruecos es el privilegio que ningún sueldo puede comprar." },
  },
];

const POSITIONS = [
  { id: 1, title: { en: "Front Office Manager", fr: "Chef de réception", es: "Jefe/a de recepción" }, dept: "front-office", location: "Arfoud", type: "full-time", langs: "FR · EN · ES" },
  { id: 2, title: { en: "Receptionist · multilingual", fr: "Réceptionniste multilingue", es: "Recepcionista multilingüe" }, dept: "front-office", location: "Merzouga", type: "full-time", langs: "FR · EN" },
  { id: 3, title: { en: "Executive Chef", fr: "Chef exécutif", es: "Chef ejecutivo/a" }, dept: "kitchen", location: "Boumalne Dades", type: "full-time", langs: "FR · AR" },
  { id: 4, title: { en: "Pastry Chef", fr: "Chef pâtissier", es: "Chef pastelero/a" }, dept: "kitchen", location: "Arfoud", type: "full-time", langs: "FR" },
  { id: 5, title: { en: "Demi Chef de Partie", fr: "Demi chef de partie", es: "Demi chef de partie" }, dept: "kitchen", location: "Sidi Ali", type: "seasonal", langs: "FR" },
  { id: 6, title: { en: "Sommelier", fr: "Sommelier", es: "Sumiller" }, dept: "fnb", location: "Arfoud", type: "full-time", langs: "FR · EN" },
  { id: 7, title: { en: "Restaurant Manager", fr: "Responsable restauration", es: "Responsable de restaurante" }, dept: "fnb", location: "Sidi Ali", type: "full-time", langs: "FR · EN" },
  { id: 8, title: { en: "Spa Therapist", fr: "Praticien·ne spa", es: "Terapeuta de spa" }, dept: "spa", location: "Sidi Ali", type: "full-time", langs: "FR · EN" },
  { id: 9, title: { en: "Hammam Attendant", fr: "Préposé·e hammam", es: "Auxiliar de hammam" }, dept: "spa", location: "Arfoud", type: "full-time", langs: "AR · FR" },
  { id: 10, title: { en: "Camp Manager — Erg Chebbi", fr: "Responsable de bivouac — Erg Chebbi", es: "Responsable de bivac — Erg Chebbi" }, dept: "bivouac", location: "Merzouga", type: "seasonal", langs: "FR · EN · ES" },
  { id: 11, title: { en: "Bivouac Host / Hostess", fr: "Hôte·sse de bivouac", es: "Anfitrión/a de bivac" }, dept: "bivouac", location: "Merzouga", type: "seasonal", langs: "FR · EN" },
  { id: 12, title: { en: "Berber-Speaking Trek Guide", fr: "Guide de trek berbérophone", es: "Guía de trekking berberófono/a" }, dept: "experiences", location: "Atlas — Ifess", type: "full-time", langs: "Berber · FR" },
  { id: 13, title: { en: "Camel Caravan Leader", fr: "Chef de caravane chamelière", es: "Líder de caravana en camello" }, dept: "experiences", location: "Erg Chebbi", type: "seasonal", langs: "AR · FR" },
  { id: 14, title: { en: "Events Producer", fr: "Productrice·teur événementiel", es: "Productor/a de eventos" }, dept: "events", location: "Arfoud", type: "full-time", langs: "FR · EN · ES" },
  { id: 15, title: { en: "AV Technician — outdoor staging", fr: "Technicien·ne audiovisuel·le", es: "Técnico/a audiovisual" }, dept: "events", location: "Erg Chebbi", type: "seasonal", langs: "FR" },
  { id: 16, title: { en: "Housekeeping Supervisor", fr: "Gouvernant·e", es: "Gobernanta" }, dept: "housekeeping", location: "Boumalne Dades", type: "full-time", langs: "AR · FR" },
  { id: 17, title: { en: "Maintenance Engineer", fr: "Ingénieur·e maintenance", es: "Ingeniero/a de mantenimiento" }, dept: "operations", location: "Multi-site", type: "full-time", langs: "FR" },
  { id: 18, title: { en: "4x4 Driver / Guide", fr: "Chauffeur·euse-guide 4x4", es: "Conductor/a-guía 4x4" }, dept: "experiences", location: "Multi-site", type: "full-time", langs: "FR · EN" },
  { id: 19, title: { en: "Concierge — luxury bookings", fr: "Concierge — réservations premium", es: "Conserje — reservas premium" }, dept: "front-office", location: "Arfoud", type: "full-time", langs: "FR · EN · ES" },
  { id: 20, title: { en: "Travel Agent — Xaluca Tours", fr: "Agent·e de voyages — Xaluca Tours", es: "Agente de viajes — Xaluca Tours" }, dept: "agency", location: "Sabadell, Barcelona", type: "full-time", langs: "ES · EN · FR" },
];

const DEPTS = [
  { id: "all", icon: Briefcase, label: { en: "All departments", fr: "Tous services", es: "Todos los departamentos" } },
  { id: "front-office", icon: Users, label: { en: "Front office", fr: "Réception", es: "Recepción" } },
  { id: "kitchen", icon: Coffee, label: { en: "Kitchen", fr: "Cuisine", es: "Cocina" } },
  { id: "fnb", icon: Coffee, label: { en: "F&B / service", fr: "Salle / service", es: "Sala y servicio" } },
  { id: "spa", icon: Sparkles, label: { en: "Spa & wellness", fr: "Spa & bien-être", es: "Spa y bienestar" } },
  { id: "bivouac", icon: Mountain, label: { en: "Bivouacs & camps", fr: "Bivouacs & camps", es: "Bivacs y campamentos" } },
  { id: "experiences", icon: Compass, label: { en: "Guides & experiences", fr: "Guides & expériences", es: "Guías y experiencias" } },
  { id: "events", icon: Sparkles, label: { en: "Events production", fr: "Production événements", es: "Producción de eventos" } },
  { id: "housekeeping", icon: Home, label: { en: "Housekeeping", fr: "Étages", es: "Pisos" } },
  { id: "operations", icon: Briefcase, label: { en: "Operations", fr: "Opérations", es: "Operaciones" } },
  { id: "agency", icon: Globe2, label: { en: "Travel agency", fr: "Agence de voyages", es: "Agencia de viajes" } },
];

const TYPE_LABEL = {
  "full-time": { en: "Full-time", fr: "CDI", es: "Indefinido" },
  "seasonal": { en: "Seasonal", fr: "Saisonnier", es: "Estacional" },
};

const WorkWithUsPage = () => {
  const { lang } = useLanguage();
  const [dept, setDept] = useState("all");
  const [type, setType] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return POSITIONS.filter((p) => {
      if (dept !== "all" && p.dept !== dept) return false;
      if (type !== "all" && p.type !== type) return false;
      if (q && !pick(p.title, lang).toLowerCase().includes(q) && !p.location.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [dept, type, query, lang]);

  return (
    <div data-testid="page-work-with-us" className="bg-[#FDFBF7]">
      {/* HERO — multi-slide (video first, then team & properties photography) */}
      <section data-testid="careers-hero">
        <PageHero
          images={[
            HERO,
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/6pvzp897_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-1.jpg",
          ]}
          alt="Work with us · Grup Xaluca"
          scope="page.work-with-us"
          overline={pick({ en: "Work with us", fr: "Travailler avec nous", es: "Trabajar con nosotros" }, lang)}
          title={pick({
            en: "Build a career between the dunes and the cedar woods.",
            fr: "Construire une carrière entre les dunes et les forêts de cèdres.",
            es: "Construir una carrera entre las dunas y los cedros.",
          }, lang)}
          sub={pick({
            en: "Grup Xaluca employs over three hundred families across nine properties in the south of Morocco. We are looking for people who believe hospitality is a craft — and who want to live where they work.",
            fr: "Le Grup Xaluca emploie plus de trois cents familles dans neuf adresses du sud du Maroc. Nous cherchons des personnes pour qui l'hospitalité est un métier d'art — et qui veulent vivre là où elles travaillent.",
            es: "Grup Xaluca emplea a más de trescientas familias en nueve propiedades del sur de Marruecos. Buscamos personas para quienes la hospitalidad sea un oficio — y que quieran vivir allí donde trabajan.",
          }, lang)}
          ctaTo="#positions"
          ctaLabel={pick({ en: "See open positions", fr: "Voir les postes ouverts", es: "Ver puestos abiertos" }, lang)}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "careers-mission", label: { en: "Mission", fr: "Mission", es: "Misión" } },
          { id: "careers-values", label: { en: "Values", fr: "Valeurs", es: "Valores" } },
          { id: "careers-evp", label: { en: "Benefits", fr: "Avantages", es: "Beneficios" } },
          { id: "positions", label: { en: "Positions", fr: "Postes", es: "Vacantes" } },
          { id: "careers-cta-final", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* MISSION */}
      <section id="careers-mission" className="py-24 md:py-32 scroll-mt-32" data-testid="careers-mission">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="overline">
              {pick({ en: "Our mission", fr: "Notre mission", es: "Nuestra misión" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
              {pick({
                en: "Show the south of Morocco — and protect the families who make it possible.",
                fr: "Faire découvrir le sud du Maroc — et protéger les familles qui le rendent possible.",
                es: "Mostrar el sur de Marruecos — y proteger a las familias que lo hacen posible.",
              }, lang)}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-7 text-[#3C342D]">
            <p className="font-serif-x text-2xl md:text-3xl leading-[1.35] text-[#2C2621]">
              {pick({
                en: "We exist to give travellers from 40 countries a quiet, dignified, deeply Moroccan welcome — and to give the people of the Tafilalet, the Atlas and the Dadès valley a reason to stay, raise their children, and build a future here.",
                fr: "Nous existons pour offrir aux voyageurs venus de 40 pays un accueil discret, digne et profondément marocain — et pour donner aux habitants du Tafilalet, de l'Atlas et de la vallée du Dadès une raison de rester, d'élever leurs enfants et de bâtir un avenir ici.",
                es: "Existimos para ofrecer a los viajeros de 40 países una acogida discreta, digna y profundamente marroquí — y para dar a la gente del Tafilalet, del Atlas y del valle del Dadès una razón para quedarse, criar a sus hijos y construir un futuro aquí.",
              }, lang)}
            </p>
            <p className="text-lg leading-relaxed text-[#5C5248]">
              {pick({
                en: "Hospitality, for us, is not an industry. It is a way of holding on to a region. Every job we open is a household stabilised, a child kept in school, a village kept alive. If you choose us, you choose that.",
                fr: "L'hospitalité, pour nous, n'est pas une industrie. C'est une manière de tenir une région. Chaque poste que nous ouvrons, c'est un foyer stabilisé, un enfant maintenu à l'école, un village maintenu vivant. Nous choisir, c'est choisir cela.",
                es: "La hospitalidad, para nosotros, no es una industria. Es una forma de sostener una región. Cada puesto que abrimos es un hogar estabilizado, un niño que sigue en la escuela, un pueblo que sigue vivo. Elegirnos es elegir eso.",
              }, lang)}
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section id="careers-values" className="py-20 md:py-24 bg-[#F2EBE1]/50 scroll-mt-32" data-testid="careers-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="overline">
              {pick({ en: "Our values", fr: "Nos valeurs", es: "Nuestros valores" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#2C2621]">
              {pick({
                en: "Six principles we hire on — and fire on.",
                fr: "Six principes sur lesquels nous recrutons — et licencions.",
                es: "Seis principios sobre los que contratamos — y despedimos.",
              }, lang)}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E8DFD2] border border-[#E8DFD2] rounded-3xl overflow-hidden">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title.en} className="bg-white p-7 md:p-8" data-testid={`careers-value-${i}`}>
                  <Icon className="w-6 h-6 text-[#C16542]" strokeWidth={1.5} />
                  <h3 className="font-serif-x text-xl md:text-2xl mt-5 leading-tight text-[#2C2621]">
                    {pick(v.title, lang)}
                  </h3>
                  <p className="mt-3 text-[15px] text-[#5C5248] leading-relaxed">
                    {pick(v.text, lang)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY HERE / EMPLOYER VALUE PROPOSITION */}
      <section id="careers-evp" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="careers-evp">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="overline !text-[#D4A373]">
                {pick({ en: "Why southern Morocco · why us", fr: "Pourquoi le sud du Maroc · pourquoi nous", es: "Por qué el sur de Marruecos · por qué nosotros" }, lang)}
              </p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {pick({
                  en: "A place few hospitality groups even reach.",
                  fr: "Un terrain que peu de groupes hôteliers atteignent.",
                  es: "Un terreno al que pocos grupos hoteleros llegan.",
                }, lang)}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base text-[#FDFBF7]/75 leading-relaxed">
                {pick({
                  en: "Most of our properties sit hours from the nearest airport, between dunes, mountains and oases. The trade-off: a quality of life and a depth of training you will not find in a city brand.",
                  fr: "La plupart de nos adresses se trouvent à des heures du premier aéroport, entre dunes, montagnes et oasis. Le contrepoint : une qualité de vie et une profondeur de formation introuvables en marque urbaine.",
                  es: "La mayoría de nuestras direcciones están a horas del aeropuerto más cercano, entre dunas, montañas y oasis. La contrapartida: una calidad de vida y una formación que no se encuentran en una marca urbana.",
                }, lang)}
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#FDFBF7]/15 border border-[#FDFBF7]/15 rounded-3xl overflow-hidden">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={b.title.en} className="bg-[#1A1513] p-7 md:p-8" data-testid={`careers-benefit-${i}`}>
                  <span className="w-11 h-11 rounded-full bg-[#D4A373]/15 text-[#D4A373] flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif-x text-xl md:text-2xl mt-5 leading-tight text-[#FDFBF7]">
                    {pick(b.title, lang)}
                  </h3>
                  <p className="mt-3 text-[14px] text-[#FDFBF7]/75 leading-relaxed">
                    {pick(b.text, lang)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* POSITIONS */}
      <section id="positions" className="py-24 md:py-32 scroll-mt-24" data-testid="careers-positions">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end gap-6 mb-10">
            <div className="lg:flex-1">
              <p className="overline">
                {pick({ en: "Open positions", fr: "Postes ouverts", es: "Puestos abiertos" }, lang)}
              </p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
                {pick({
                  en: "Find a role across our nine properties.",
                  fr: "Trouvez un poste dans nos neuf adresses.",
                  es: "Encuentre un puesto en nuestras nueve direcciones.",
                }, lang)}
              </h2>
            </div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#5C5248]" data-testid="careers-count">
              {filtered.length} / {POSITIONS.length} {pick({ en: "roles", fr: "postes", es: "puestos" }, lang)}
            </div>
          </div>

          {/* Search + type filters */}
          <div className="grid md:grid-cols-12 gap-3 mb-3">
            <div className="md:col-span-7 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A89A87]" strokeWidth={1.8} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={pick({ en: "Search a role or a location…", fr: "Chercher un poste ou un lieu…", es: "Buscar un puesto o un lugar…" }, lang)}
                className="w-full bg-white border border-[#E8DFD2] focus:border-[#C16542] outline-none rounded-2xl pl-9 pr-9 h-12 text-sm"
                data-testid="careers-search"
              />
              {query && (
                <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#5C5248] hover:text-[#C16542]" data-testid="careers-search-clear" aria-label={pick({ en: "Clear", fr: "Effacer", es: "Borrar" }, lang)}>
                  <X className="w-3.5 h-3.5" strokeWidth={1.8} />
                </button>
              )}
            </div>
            <div className="md:col-span-5 flex gap-2">
              {["all", "full-time", "seasonal"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                    type === t
                      ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]"
                      : "bg-white text-[#5C5248] border-[#E8DFD2] hover:border-[#C16542]"
                  }`}
                  data-testid={`careers-type-${t}`}
                >
                  {t === "all"
                    ? pick({ en: "All", fr: "Tous", es: "Todos" }, lang)
                    : pick(TYPE_LABEL[t], lang)}
                </button>
              ))}
            </div>
          </div>

          {/* Department chips */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="careers-dept-filters">
            {DEPTS.map((d) => {
              const Icon = d.icon;
              const on = dept === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDept(d.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                    on ? "bg-[#C16542] text-[#FDFBF7] border-[#C16542]" : "bg-white text-[#5C5248] border-[#E8DFD2] hover:border-[#C16542]"
                  }`}
                  data-testid={`careers-dept-${d.id}`}
                  aria-pressed={on}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                  {pick(d.label, lang)}
                </button>
              );
            })}
          </div>

          {/* List */}
          {filtered.length > 0 ? (
            <ul className="space-y-3">
              {filtered.map((p) => (
                <li
                  key={p.id}
                  className="group bg-white border border-[#E8DFD2] rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 hover:border-[#C16542] hover:shadow-[0_8px_24px_-12px_rgba(44,38,33,0.18)] transition-all"
                  data-testid={`careers-job-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif-x text-xl md:text-2xl text-[#2C2621] leading-tight">
                      {pick(p.title, lang)}
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[#5C5248]">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 text-[#C16542]" strokeWidth={1.6} />
                        {p.location}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-[#C16542]" strokeWidth={1.6} />
                        {pick(TYPE_LABEL[p.type], lang)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Globe2 className="w-3 h-3 text-[#C16542]" strokeWidth={1.6} />
                        {p.langs}
                      </span>
                    </div>
                  </div>
                  <a
                    href={`mailto:careers@xaluca.com?subject=Application%20%E2%80%94%20${encodeURIComponent(pick(p.title, lang))}%20(%23${p.id})`}
                    className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#2C2621] text-[#FDFBF7] hover:bg-[#C16542] px-5 py-3 text-[10px] tracking-[0.3em] uppercase transition-colors rounded-xl"
                    data-testid={`careers-apply-${p.id}`}
                  >
                    {pick({ en: "Apply", fr: "Postuler", es: "Solicitar" }, lang)}
                    <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className="py-16 text-center" data-testid="careers-empty">
              <p className="font-serif-x text-2xl text-[#2C2621]">
                {pick({
                  en: "No role matches these filters today — write to us anyway.",
                  fr: "Aucun poste ne correspond à ces filtres aujourd'hui — écrivez-nous tout de même.",
                  es: "Ningún puesto coincide hoy con estos filtros — escríbanos de todos modos.",
                }, lang)}
              </p>
              <a
                href="mailto:careers@xaluca.com?subject=Spontaneous%20application"
                className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#C16542] border-b border-[#C16542]/40 pb-1"
              >
                {pick({ en: "Spontaneous application", fr: "Candidature spontanée", es: "Candidatura espontánea" }, lang)}
                <Mail className="w-3.5 h-3.5" strokeWidth={1.6} />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section id="careers-cta-final" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="careers-cta-final">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Briefcase className="w-7 h-7 mx-auto text-[#D4A373]" strokeWidth={1.5} />
          <h2 className="font-serif-x mt-6 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            {pick({
              en: "Don't see your dream role? Write to us anyway.",
              fr: "Vous ne voyez pas votre poste rêvé ? Écrivez-nous quand même.",
              es: "¿No ve su puesto ideal? Escríbanos igualmente.",
            }, lang)}
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-[#FDFBF7]/75 leading-relaxed">
            {pick({
              en: "We open new positions every season. Send your CV, tell us where in the south of Morocco you would like to live, and we will keep the letter.",
              fr: "Nous ouvrons de nouveaux postes à chaque saison. Envoyez votre CV, dites-nous où dans le sud du Maroc vous voudriez vivre, et nous garderons la lettre.",
              es: "Abrimos nuevos puestos cada temporada. Envíe su CV, díganos en qué parte del sur de Marruecos le gustaría vivir, y guardaremos la carta.",
            }, lang)}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:careers@xaluca.com?subject=Spontaneous%20application"
              className="inline-flex items-center justify-center gap-2 bg-[#C16542] text-[#FDFBF7] hover:bg-[#D4A373] hover:text-[#1A1513] px-7 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors"
              data-testid="careers-cta-email"
            >
              <Mail className="w-3.5 h-3.5" strokeWidth={1.6} />
              careers@xaluca.com
            </a>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 border border-[#FDFBF7]/40 px-7 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#FDFBF7] hover:text-[#1A1513] transition-colors"
              data-testid="careers-cta-about"
            >
              {pick({ en: "Read our story", fr: "Lire notre histoire", es: "Lea nuestra historia" }, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkWithUsPage;
